/**
 * Auth Store
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { devtools } from 'zustand/middleware';
import { setAuthTokens, clearAuthTokens } from '@/lib/axios';
import type { User } from '@/types';
import type { Role } from '@/config/constants';
import { ROLE_PRIORITY } from '@/config/constants';

interface AuthState {
  // State
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Computed
  permissions: string[];
  role: Role | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateAccessToken: (accessToken: string) => void;
  setLoading: (isLoading: boolean) => void;
  
  // Permission checks
  hasPermission: (permission: string) => boolean;
  hasRole: (role: Role) => boolean;
  hasMinRole: (minRole: Role) => boolean;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        isLoading: true,
        permissions: [],
        role: null,
        
        // Set user
        setUser: (user) => {
          set({
            user,
            permissions: user?.permissions ?? [],
            role: user?.role ?? null,
          });
        },
        
        // Set tokens
        setTokens: (accessToken, refreshToken) => {
          setAuthTokens(
            accessToken,
            refreshToken,
            (newToken) => get().updateAccessToken(newToken),
            () => get().logout()
          );
          set({ accessToken, refreshToken });
        },
        
        // Login
        login: (user, accessToken, refreshToken) => {
          setAuthTokens(
            accessToken,
            refreshToken,
            (newToken) => get().updateAccessToken(newToken),
            () => get().logout()
          );
          set({
            user,
            accessToken,
            refreshToken,
            isAuthenticated: true,
            isLoading: false,
            permissions: user.permissions,
            role: user.role,
          });
        },
        
        // Logout
        logout: () => {
          clearAuthTokens();
          set({
            user: null,
            accessToken: null,
            refreshToken: null,
            isAuthenticated: false,
            isLoading: false,
            permissions: [],
            role: null,
          });
        },
        
        // Update access token (after refresh)
        updateAccessToken: (accessToken) => {
          set({ accessToken });
        },
        
        // Set loading
        setLoading: (isLoading) => {
          set({ isLoading });
        },
        
        // Check permission
        hasPermission: (permission) => {
          const { permissions, role } = get();
          // Admin has all permissions
          if (role === 'admin') return true;
          return permissions.includes(permission);
        },
        
        // Check exact role
        hasRole: (role) => {
          return get().role === role;
        },
        
        // Check minimum role
        hasMinRole: (minRole) => {
          const { role } = get();
          if (!role) return false;
          return ROLE_PRIORITY[role] >= ROLE_PRIORITY[minRole];
        },
      }),
      {
        name: 'reinfo-auth',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
          permissions: state.permissions,
          role: state.role,
        }),
        onRehydrateStorage: () => (state) => {
          if (state) {
            // If we have tokens, set them up
            if (state.accessToken && state.refreshToken) {
              setAuthTokens(
                state.accessToken,
                state.refreshToken,
                (newToken) => state.updateAccessToken(newToken),
                () => state.logout()
              );
            }
            state.setLoading(false);
          }
        },
      }
    ),
    { name: 'AuthStore' }
  )
);

// Selectors
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectUser = (state: AuthState) => state.user;
export const selectRole = (state: AuthState) => state.role;
export const selectPermissions = (state: AuthState) => state.permissions;
